import { useQueryClient } from "@tanstack/react-query";
import { decideApproval, useApprovals } from "../../lib/queries";
import { relativeTime } from "../../lib/format";

export default function ApprovalDetailDrawer({ id }: { id: string }) {
  const { data: approvals = [] } = useApprovals();
  const qc = useQueryClient();
  const a = approvals.find((x) => x.id === id);
  if (!a) return <div className="px-4 py-3 text-xs text-text-quiet">approval not found</div>;

  const decide = async (status: "approved" | "rejected") => {
    await decideApproval(a.id, status);
    qc.invalidateQueries({ queryKey: ["approvals"] });
  };

  return (
    <div className="flex flex-col gap-3 p-4">
      <div>
        <span
          className="font-data text-micro"
          style={{ letterSpacing: "0.13em", color: "var(--text-quiet)" }}
        >
          APPROVAL · {a.project}
        </span>
        <h2 className="mt-1 text-base font-semibold text-text">{a.action}</h2>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Field label="Class" value={a.class} />
        <Field label="Status" value={a.status} />
        <Field label="Agent" value={a.agent} />
        <Field label="Requested" value={relativeTime(a.requested_at)} />
      </div>
      {a.payload ? (
        <div className="rounded border border-border bg-surface p-2">
          <span
            className="font-data text-micro"
            style={{ letterSpacing: "0.1em", color: "var(--text-quiet)" }}
          >
            PAYLOAD
          </span>
          <pre
            className="mt-1 overflow-x-auto text-xs text-text-dim"
            style={{ fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace" }}
          >
            {JSON.stringify(a.payload, null, 2)}
          </pre>
        </div>
      ) : null}
      {a.status === "pending" ? (
        <div className="flex gap-2 border-t border-border pt-3">
          <button
            type="button"
            onClick={() => decide("approved")}
            className="font-data flex-1 rounded-sm border px-3 py-1.5 text-xs"
            style={{
              borderColor: "var(--green)",
              backgroundColor: "rgba(126,198,153,0.1)",
              color: "var(--green)",
            }}
          >
            Approve
          </button>
          <button
            type="button"
            onClick={() => decide("rejected")}
            className="font-data flex-1 rounded-sm border px-3 py-1.5 text-xs"
            style={{
              borderColor: "var(--red)",
              backgroundColor: "rgba(207,102,121,0.08)",
              color: "var(--red)",
            }}
          >
            Reject
          </button>
        </div>
      ) : (
        <div className="text-xs text-text-quiet">
          Decided {relativeTime(a.decided_at)} — {a.status}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col rounded border border-border bg-surface px-2 py-1.5">
      <span
        className="font-data text-micro"
        style={{ letterSpacing: "0.1em", color: "var(--text-quiet)" }}
      >
        {label.toUpperCase()}
      </span>
      <span className="text-xs text-text">{value}</span>
    </div>
  );
}
